import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import type { BattleState } from '@somnhold/sim';
import { pinMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';

/** A sluice: two posts, the gate between, a wheel on top. */
const GATE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 14V4M13 14V4M2 4h12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" fill="none"/><rect x="5" y="7" width="6" height="5" rx="0.6" fill="currentColor"/><circle cx="8" cy="2.6" r="1.6" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>';
/** A handful of salt: grains in a heap. */
const SALT = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 13.5c2-4.5 4-6.5 6-6.5s4 2 6 6.5z" fill="currentColor"/><circle cx="5" cy="4.5" r="1" fill="currentColor"/><circle cx="8.5" cy="3" r="1" fill="currentColor"/><circle cx="11.5" cy="5" r="0.9" fill="currentColor"/></svg>';
/** A pool and its level (water in a square). */
const POOL = '<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="2" y="3" width="12" height="10" rx="1" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M3 9c1.5 0 1.5-1 3-1s1.5 1 3 1 1.5-1 3-1 1.5 1 1 1V12H3z" fill="currentColor"/></svg>';

/** Where the salt flats hang their marks (render's SaltLayer anchors, world points). */
export interface SaltAnchors {
  gates: THREE.Vector3[];
  altar: THREE.Vector3 | null;
  door: THREE.Vector3 | null;
}

/**
 * The salt flats on screen (docs/sendas/paladin.md, chapter 2), inside a path's own plate (game/senda-hud.ts), short
 * enough that a phone's plate stays under a quarter of the screen:
 *
 * - the fog: how far Ser Marit is from the gate house is the plate's own bar.
 * - the gates: the altar's pool's marks (four pips, going out as it drains), the sea's next return counting down (red
 *   as it rises), the returns spent of three. Over the board, a mark on each gate, tappable (the hero goes and turns
 *   it), its ring filling as it turns; a turned gate reads «trabada» until the sea frees it, «corre» while its water
 *   runs.
 * - the test: Marit's salt (ready, or in how long) and its button: pressed, the next tap on the board is where she
 *   throws it; the old stalker's leap, red, while it crouches on her («Ponete en la marca»).
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md).
 */
export class SaltHud {
  readonly panel: HTMLElement;
  private kind: 'fog' | 'sluice' | 'hold';
  private v = new THREE.Vector3();
  private pips: HTMLElement[] = [];
  private poolRow = h('div.bh-row.sa-pool');
  private back = h('span.al-wave');
  private backRow = h('div.bh-row.al-waverow');
  private returns: HTMLElement[] = [];
  private gates: { el: HTMLButtonElement; state: HTMLElement }[] = [];
  private salt = h('span.al-wave');
  private saltBtn: HTMLButtonElement | null = null;
  private saltRow = h('div.bh-row.sa-saltrow');
  private leapRow = h('div.bh-row.al-waverow.warn');
  private aiming = false;
  private key = '';

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
    private aim: (on: boolean) => void,
  ) {
    const r = s.rules?.senda?.salt;
    this.kind = r?.kind ?? 'fog';
    this.panel = this.kind === 'fog' ? this.buildFog() : this.kind === 'sluice' ? this.buildSluice(s) : this.buildHold();
  }

  /** The fog: its way to the house is the plate's own bar and count (nothing more to show). */
  private buildFog(): HTMLElement {
    return h('div');
  }

  private buildSluice(s: BattleState): HTMLElement {
    const r = s.rules?.senda?.salt;
    if (!r?.gates || !r.sluice) return h('div');
    this.pips = Array.from({ length: r.cap }, () => h('i.on'));
    this.poolRow.append(h('span.bh-ico', { html: POOL }), h('span.bh-label', {}, t('senda.salt.altar')), h('span.al-pips.sa-water', {}, ...this.pips));
    this.backRow.append(h('span.bh-ico', { html: ICONS.hourglass }), h('span.bh-label', {}, t('senda.salt.sea')), this.back);
    this.returns = Array.from({ length: r.sluice.lose }, () => h('i'));
    const ret = h('div.bh-row', {}, h('span.bh-ico', { html: ICONS.skull }), h('span.bh-label', {}, t('senda.salt.returns')), h('span.al-pips.sa-returns', {}, ...this.returns));
    this.gates = r.gates.map((_, i) => {
      const state = h('small.al-state');
      const el = h(
        'button.al-stone.sa-gate',
        { type: 'button', 'data-test': `salt-gate-${i}`, 'aria-label': t('senda.salt.gateHint'), onpointerup: (e: Event) => (e.stopPropagation(), this.mark(i)) },
        h('span.al-ring'),
        h('span.al-glyph', { html: GATE }),
        state,
      ) as HTMLButtonElement;
      this.board.append(el);
      return { el, state };
    });
    return h('div.bh.al-canal', {}, this.poolRow, this.backRow, ret);
  }

  private buildHold(): HTMLElement {
    const btn = h('button.sa-throw', { type: 'button', 'data-test': 'salt-aim', 'aria-label': t('senda.salt.aim'), onpointerup: (e: Event) => (e.stopPropagation(), this.setAim(!this.aiming)) }, h('span', { html: SALT }), h('b', {}, t('senda.salt.throw'))) as HTMLButtonElement;
    this.saltBtn = btn;
    this.saltRow.append(h('span.bh-ico', { html: SALT }), h('span.bh-label', {}, t('senda.salt.salt')), this.salt, btn);
    this.leapRow.append(h('span.bh-ico', { html: ICONS.skull }), h('span.al-wave.sa-leap', {}, t('senda.salt.leap')));
    this.leapRow.hidden = true;
    return h('div.bh.al-canal', {}, this.saltRow, this.leapRow);
  }

  /** The salt's aim on or off (the button, and the board's next tap). */
  setAim(on: boolean): void {
    this.aiming = on;
    this.saltBtn?.classList.toggle('on', on);
    this.aim(on);
  }

  update(s: BattleState): void {
    const r = s.rules?.senda?.salt;
    const w = s.senda?.salt;
    if (!r || !w) return;
    if (this.kind === 'fog') return;
    if (this.kind === 'sluice' && r.sluice) {
      const lv = w.level[r.altar] ?? 0;
      const flowing = w.flows.map((f) => f.g);
      const key = `${lv}|${w.locked.join('')}|${flowing.join(',')}|${w.returns}|${w.dried}`;
      if (key !== this.key) {
        this.key = key;
        this.pips.forEach((p, i) => p.classList.toggle('on', i < lv));
        this.poolRow.classList.toggle('full', lv === 0);
        this.returns.forEach((p, i) => p.classList.toggle('on', i < w.returns));
        this.gates.forEach((g, i) => {
          const locked = w.locked[i] === 1;
          const runs = flowing.includes(i);
          g.el.classList.toggle('shut', locked && !runs);
          g.el.classList.toggle('runs', runs);
          g.state.textContent = runs ? t('senda.salt.running') : locked ? t('senda.salt.locked') : '';
          g.state.hidden = !runs && !locked;
        });
      }
      const left = Math.max(0, Math.ceil((w.back - s.waveT) / TICK_HZ));
      const rising = w.dried < 0 && w.back - s.waveT <= r.sluice.warn;
      const text = w.dried >= 0 ? t('senda.salt.dry') : rising ? t('senda.salt.coming') : t('senda.salt.backIn', { s: left });
      if (this.back.textContent !== text) this.back.textContent = text;
      this.backRow.classList.toggle('warn', rising);
      this.gates.forEach((g, i) => {
        const sent = w.job === i;
        g.el.classList.toggle('sent', sent);
        g.el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, r.sluice?.turn ?? 1)).toFixed(2) : '0');
        g.el.disabled = s.result !== 'running';
      });
      return;
    }
    if (r.hold) {
      const m = s.senda?.npcs.find((n) => n.id === 'marit');
      const ready = s.tick >= w.ready && !w.throwing && !!m && m.hp > 0;
      const text = !m || m.hp <= 0 ? t('senda.salt.noMarit') : ready ? t('senda.salt.saltReady') : t('senda.salt.saltIn', { s: Math.max(1, Math.ceil((w.ready - s.tick) / TICK_HZ)) });
      if (this.salt.textContent !== text) this.salt.textContent = text;
      this.saltRow.classList.toggle('ready', ready);
      if (this.saltBtn) this.saltBtn.disabled = !ready || s.result !== 'running';
      if (!ready && this.aiming) this.setAim(false);
      this.leapRow.hidden = !(w.old.id && w.old.mode === 1);
    }
  }

  place(camera: THREE.Camera, w: number, ht: number, a: SaltAnchors | null): void {
    if (!a || this.kind !== 'sluice') return;
    this.gates.forEach((g, i) => {
      const p = a.gates[i];
      if (!p) return setStyle(g.el, 'display', 'none');
      this.v.copy(p).project(camera);
      if (this.v.z > 1) return setStyle(g.el, 'display', 'none');
      setStyle(g.el, 'display', '');
      pinMark(g.el, (this.v.x * 0.5 + 0.5) * w, (-this.v.y * 0.5 + 0.5) * ht);
    });
  }
}
